import { useT } from "../../lib/use-t";

interface SchrijvenWordMeterProps {
  words: number;
  /** Minimum word target (A2 e-mail floor; static constant from the caller). */
  min: number;
  /** Compact variant for the mobile bottom bar (`{words}/{min}`, 70px track). */
  compact?: boolean;
}

/**
 * Word-count meter for the runner's bottom bar. Track fill scales with
 * words/min; turns pass-green and shows a "minimum gehaald" helper once the
 * floor is reached. The fill width is a layout value, not animated — only the
 * colour transitions (transform/opacity rule, brand.md §9).
 */
export function SchrijvenWordMeter({ words, min, compact = false }: SchrijvenWordMeterProps) {
  const t = useT();
  const reached = words >= min;
  const pct = min > 0 ? Math.min(100, Math.round((words / min) * 100)) : 0;

  return (
    <div className="flex items-center gap-2.5">
      <div
        className="h-[7px] rounded-full bg-[var(--panel-soft)] overflow-hidden shrink-0"
        style={{ width: compact ? 70 : 120 }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={min}
        aria-valuenow={Math.min(words, min)}
        aria-label={t("schrijven.runner.wordMeterFmt", { words, min })}
      >
        <span
          className="block h-full rounded-full bg-[var(--pass)] transition-colors duration-200"
          style={{ width: `${pct}%` }}
        />
      </div>
      {compact ? (
        <span className="font-mono-badge text-[13px] tabular-nums text-[var(--ink-muted)]">
          <b className="text-[var(--pass)]">{words}</b>/{min}
        </span>
      ) : (
        <>
          <span className="font-mono-badge text-[13px] tabular-nums text-[var(--ink-muted)]">
            <b className="text-[var(--pass)]">{words}</b> / {min}{" "}
            {t("schrijven.runner.woordenLabel")}
          </span>
          {reached ? (
            <span className="font-mono-badge text-[11px] text-[var(--ink-faint)]">
              {t("schrijven.runner.minReached")}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
